<template>
  <div>
    <div class="page-header">
      <div class="add-item d-flex">
        <div class="page-title text-uppercase">
          <h4>{{ $t('debit_voucher') }}</h4>
        </div>
      </div>
      <ul class="table-top-head">
        <li>
          <div class="page-btn">
            <router-link :to="{name:'DebitVoucher'}" class="btn btn-added">
              <span>Back</span>
            </router-link>
          </div>
        </li>
        <li>
          <a data-bs-toggle="tooltip" data-bs-placement="top" title="Collapse" id="collapse-header"><i class="ti ti-chevron-up"></i></a>
        </li>
      </ul>
    </div>

    <div class="card table-list-card p-3 bg-white fs-7">
    <form @submit.prevent="submitBatch" enctype="multipart/form-data">
      <!-- Header Form Section -->
      <div class="row gx-4 mb-3 voucher-header">
        <div class="col-md-6">
          <div class="d-flex align-items-center">
            <label class="header-label form-label small mb-0">{{$t("voucher_no")}}</label>
            <input type="text" class="form-control header-field" v-model="batches.voucher_no" readonly/>
          </div>
          <div class="header-error"></div>
        </div>

        <div class="col-md-6">
          <div class="d-flex align-items-center">
            <label class="header-label form-label small mb-0">{{$t("voucher_date")}}</label>
            <input type="date" class="form-control header-field" v-model="batches.voucher_date" @change="fetchReceivingNo" />
          </div>
          <div class="header-error">{{ errors.voucher_date }}</div>
        </div>

        <div class="col-md-6">
          <div class="d-flex align-items-center">
            <label class="header-label form-label small mb-0">{{ $t('party_type_optional') }}</label>
            <FormDropdown
              v-model="party_type"
              :options="partyTypeOptions"
              clearable
              class="header-field"
            />
          </div>
          <div class="header-error"></div>
        </div>

        <!-- Space is kept while no party type is chosen, so picking one doesn't shift the form. -->
        <div class="col-md-6" :style="{ visibility: party_type ? 'visible' : 'hidden' }">
          <div class="d-flex align-items-center">
            <label class="header-label form-label small mb-0">
              {{ party_type ? $t(party_type) : '' }} <span class="text-danger">*</span>
            </label>
            <FormDropdown
              v-model="party_id"
              :options="partyOptions"
              :placeholder="$t(party_type === 'supplier' ? 'select_supplier' : 'select_customer')"
              :searchable="true"
              clearable
              class="header-field"
            />
          </div>
          <div class="header-error">{{ errors.party_id }}</div>
        </div>
      </div>

      <!-- Item Input Section -->
      <div class="row g-2 mb-3 align-items-start fs-7">
        <div class="col">
          <div class="position-relative">
            <!-- Search Input -->
            <input
            type="text"
            v-model="query"
            @input="debouncedSearch"
            :placeholder="$t('search_item_by_name_code')"
            class="form-control"
            @keydown="handleKeys"
          />

            <!-- Dropdown Search Results -->
            <div
              v-if="query"
              class="search-results shadow rounded bg-white position-absolute mt-2"
              style="width:450px; max-height: 450px; overflow-y: auto; z-index: 1050; border: 1px solid #e0e0e0;"
              :class="{ 'd-block': results.length > 0, 'd-none': results.length === 0 }"
            >

            <!-- Results List -->
            <ul class="list-group list-group-flush">
              <li
                v-for="(item, index) in results"
                :key="item.id"
                class="list-group-item d-flex justify-content-between align-items-center"
                :class="{ 'bg-primary text-white': index === itemIndex }"
                style="padding: 10px; font-size: 0.95rem; cursor: pointer;"
              >
                <!-- Clickable Product Info -->
                <div
                  class="flex-grow-1 d-flex flex-column"
                  @click="selectItem(item)"
                  style="cursor: pointer;"
                >
                  <div class="d-flex align-items-center gap-2">

                    <!-- Item Info -->
                    <div class="d-flex flex-column">
                      <strong class="text-dark">{{ item.node }} - {{ item.account_id }}</strong>
                    </div>
                  </div>
                </div>
              </li>
            </ul>

            <!-- No Results -->
            <div v-if="!results.length" class="text-center text-muted py-4" style="font-size: 1rem;">
              No results found
            </div>
          </div>
          </div>
        </div>

        <div class="col">
          <input type="text" class="form-control" :placeholder="$t('narration')" v-model="batches.narration" />
          <div class="text-danger mt-1" style="height:0.50em;">
            <span v-if="errors.narration">{{ errors.narration }}</span>
          </div>
        </div>

        

        <!-- <div class="col">
          <input type="number" class="form-control" :placeholder="$t('quantity')" v-model="batches.quantity" />
          <div class="text-danger mt-1" style="height:0.50em;">
            <span v-if="errors.quantity">{{ errors.quantity }}</span>
          </div>
        </div> -->


        <div class="col">
          <input type="number" class="form-control" :placeholder="$t('amount')" v-model="batches.amount" />
          <div class="text-danger mt-1" style="height:0.50em;">
            <span v-if="errors.amount">{{ errors.amount }}</span>
          </div>
        </div>

        <div class="col-auto">
          <button type="button" class="btn btn-info" @click="addItem">{{$t('add')}}</button>
        </div>
      </div>

      <!-- Items Table -->
      <div class="table-responsive mb-3 fixed-table">
        <table class="table table-bordered table-striped">
          <thead class="table-light">
            <tr>
              <th>{{ $t("expense") }}</th>
              <th>{{ $t("party") }}</th>
              <th>{{ $t("narration") }}</th>
              <!-- <th>{{ $t("quantity") }}</th> -->
              <th>{{ $t("amount") }}</th>
              <th>{{ $t("action") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in addedItems" :key="index">
              <td>{{ item.item_name }}</td>
              <td>
                <template v-if="item.party_type">
                  <span class="badge bg-light text-dark me-1">{{ $t(item.party_type) }}</span>{{ item.party_name }}
                </template>
                <span v-else>—</span>
              </td>
              <td>{{ item.narration }}</td>
              <!-- <td>{{ item.quantity }}</td> -->
              <td>{{ item.amount }}</td>
              <td>
                <button type="button" class="btn btn-sm btn-danger" @click="removeItem(index)">Delete</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Payment Type -->
      <div class="d-flex justify-content-between align-items-start mt-4">
        <div class="d-flex gap-2 flex-grow-1">
          <div class="d-flex flex-column">
            <label class="form-label">
              {{$t('payment_type')}} <span class="text-danger">*</span>
            </label>
            <FormDropdown
              v-model="cash_type"
              :options="formattedcashtypes"
              style="width: 200px;"
            />
            <div v-if="errors.cash_type" class="text-danger">{{ errors.cash_type }}</div>
          </div>

          <div v-if="isBank" class="d-flex flex-column">
            <label class="form-label">
              {{$t('bank_account')}} <span class="text-danger">*</span>
            </label>
            <FormDropdown
              v-model="bank_account"
              :options="formattedbankaccounts"
              style="width: 200px;"
            />
            <div v-if="errors.bank_account" class="text-danger">{{ errors.bank_account }}</div>
          </div>

          <!-- <div class="col-auto d-flex flex-column">
            <label class="form-label">{{$t('reference_no')}} </label>
            <input
              type="text"
              v-model="reference_no"
              class="form-control"
              :placeholder= "$t('enter_reference_no')"
              style="width: 200px;"
            />
            <div v-if="errors.reference_no" class="text-danger">{{ errors.reference_no }}</div>
          </div> -->

          <!-- total, paid and due section -->
          <div class="col-auto ms-auto d-flex flex-column gap-2">
            <div class="d-flex align-items-center gap-1">
              <label class="form-label mb-0" style="width: 35px;">{{$t('total')}}</label>
              <input type="text" class="form-control text-end" :value="totalAmount" disabled />
            </div>
          </div>
        </div>
      </div>

      <div class="d-flex justify-content-end mt-3">
        <button type="button" class="btn btn-cancel me-2" @click="cancelForm">{{ $t('cancel') }}</button>
        <button type="submit" class="btn btn-submit" :disabled="saving">{{ $t('submit') }}</button>
      </div>
    </form>
  </div>

  
</div>
</template>

<script>
import apiUrls from "@/urls";
import Vue3Datepicker from 'vue3-datepicker';
import FormDropdown from "@/components/common/FormDropdown.vue";
import feather from "../../../../public/assets/js/feather.min";
import { useToast } from 'vue-toastification';
import debounce from 'lodash.debounce'
import { apiErrorMessage } from '@/helpers/apiError';
import { fetchVoucherHeads, searchVoucherHeads } from '@/helpers/voucherHeads';

export default {
  components: {
    Vue3Datepicker,
    FormDropdown,
  },

  setup() {
    const toast = useToast()
    return {toast}
  },

  data() {
    return {
      batches: {
        item: null, 
        narration: '', 
        amount: '',
        // quantity: null,
        voucher_no: '', 
        voucher_date: new Date().toISOString().split('T')[0],
        transaction_category:1,
        type: 'debit',
        flag:'dr',

      },
      items: [],
      warehouses: [],
      suppliers: [],
      cash_types: [],
      errors: {},
      errorMessage: "",
      saving: false,
      query: "",
      results: [],
      itemIndex: 0,
      selectedItem: null,
      addedItems: [],
      bank_accounts: [],
      // paid: 0,
      reference_no: '',
      selectedSupplier: null,
      // due_balance: 0,
      cash_type: null,
      bank_account: null,
      showDueModal: false,
      dueModalResolver: null,
      debouncedSearch: null,
      headsRequest: null,
      transaction_category:[],
      party_type: '',
      party_id: null,
      customers: [],
    };
  },

  mounted() {
    this.fetchDropdownData();
    this.fetchTransactionCategory();
    this.fetchParties();
  },

  computed: {
    formattedItems() {
      return this.results.map(item => ({ id: item.id, text: item.node }));
    },

    formattedTransaction() {
      return this.transaction_category.map(transac => ({ id: transac.id, text: transac.category_name }));
    },

    formattedsuppliers() {
      return this.suppliers.map(supplier => ({
        id: supplier.id,
        text: supplier.phone_number ? `${supplier.name} (${supplier.phone_number})` : supplier.name,
      }));
    },

    formattedcustomers() {
      return this.customers.map(customer => ({
        id: customer.id,
        text: customer.customer_phone
          ? `${customer.customer_name} (${customer.customer_phone})`
          : customer.customer_name,
      }));
    },

    partyTypeOptions() {
      return [
        { id: 'customer', text: this.$t('customer') },
        { id: 'supplier', text: this.$t('supplier') },
      ];
    },

    partyOptions() {
      if (this.party_type === 'customer') return this.formattedcustomers;
      if (this.party_type === 'supplier') return this.formattedsuppliers;
      return [];
    },

    formattedcashtypes() {
      return this.cash_types.map(cash_type => ({ id: cash_type.id, text: cash_type.name }));
    },

    formattedbankaccounts() {
      return this.bank_accounts.map(bank_account => ({ id: bank_account.id, text: bank_account.name }));
    },

    isBank() {
      const val = this.cash_type;
      const id = val && typeof val === 'object' ? val.id : val;
      return String(id) === '1010102';
    },

    // Total of all batch amounts
    totalAmount() {
      return this.addedItems.reduce((sum, item) => {
        const amount = parseFloat(item.amount) || 0;
        return sum + amount;
      }, 0).toFixed(2);
    },

    // Due amount = Total - Paid
    // dueAmount() {
    //   const total = parseFloat(this.totalAmount) || 0;
    //   const paid = parseFloat(this.paid) || 0;
    //   const due = total - paid;
    //   return (due < 0 ? 0 : due).toFixed(2);
    // }
  },

  methods: {
      async fetchDropdownData() {
        try {
          const [
            cash_typeRes, receive_noRes, bank_accRes,
          ] = await Promise.all([
            this.$axios.get(apiUrls.batch_cash_type.get),
            this.$axios.get(apiUrls.journal_voucher_number.get, { params: { voucher_date: this.batches.voucher_date } }),
            this.$axios.get(apiUrls.batch_bank_account.get)
          ]);

          
          this.cash_types = cash_typeRes?.data || [];
          this.batches.voucher_no = receive_noRes?.data?.voucher_no || '';
          this.bank_accounts = bank_accRes?.data || [];

          if (this.cash_types.length > 0 && !this.cash_type) {
            this.cash_type = this.cash_types[0].id;
          }

          if (this.bank_accounts.length > 0 && !this.bank_account) {
            this.bank_account = this.bank_accounts[0].id;
          }

        } catch (error) {
          console.error('Error loading dropdown data:', error);
        }
      },

      async searchItems() {
        if (!this.query) {
          this.results = []
          return
        }

        try {
          if (!this.headsRequest) this.headsRequest = fetchVoucherHeads(this.$axios);
          this.results = searchVoucherHeads(await this.headsRequest, this.query);
          this.itemIndex = 0;

        //   if (
        //   results.length === 1 
        // ) {
        //   await this.selectItem(results[0])
        //   return
        // }

          // render feather icons
          await this.$nextTick(() => {
            if (window.feather) feather.replace()
          })
        } catch (error) {
          console.error('Search error:', error)
          this.headsRequest = null
          this.results = []
        }
      },

      selectItem(item) {
        console.log('SelectItem:::',item)
        this.selectedItem = item
        this.query = item.node
        this.batches.item = item.account_id
        this.results = []
        this.itemIndex = 0;
      },

      handleKeys(event) {
        if (event.key === 'ArrowDown') {
          this.itemIndex = Math.min(this.itemIndex + 1, this.results.length - 1);
          event.preventDefault();
        } else if (event.key === 'ArrowUp') {
          this.itemIndex = Math.max(this.itemIndex - 1, 0);
          event.preventDefault();
        } else if (event.key === 'Enter') {
          const selectedProduct = this.results[this.itemIndex];
          if (selectedProduct) {
            this.selectItem(selectedProduct);
          }
          event.preventDefault();
        }
      },

      addItem() {
        if (!this.selectedItem) {
          this.toast.warning("Please select an item first.");
          return;
        }

        // Frontend validation before adding to list
        this.errors = {};
        const requiredFields = [
          "item", "amount", "voucher_date", 'voucher_no'
        ];
        requiredFields.forEach(field => {
          if (!this.batches[field]) this.errors[field] = this.$t('error.required');
        });

        if (this.party_type && !this.party_id) {
          this.errors.party_id = this.$t('error.required');
        }

        if (Object.keys(this.errors).length > 0) {
          return;
        }

        // Store full batch data in addedItems
        const party = this.partyOptions.find((option) => option.id === this.party_id);
        const newItem = {
          ...this.batches,
          id: this.selectedItem.id,
          item_name: this.selectedItem.node,
          item_code: this.selectedItem.account_id,
          party_type: this.party_type || null,
          party_id: this.party_type ? this.party_id : null,
          party_name: party ? party.text : '',
        };

        this.addedItems.push(newItem);

        // Reset only row fields, not whole form structure
        this.batches = {
          item: null,
          narration: null,
          type: 'debit',
          flag: 'dr',
          amount: null,
          voucher_no: this.batches.voucher_no, 
          voucher_date: this.batches.voucher_date,
          transaction_category: this.batches.transaction_category,
        };
        this.errors = {};
        this.query = "";
        this.selectedItem = null;
      },


      async fetchReceivingNo() {
        try {
          let res = await this.$axios.get(apiUrls.journal_voucher_number.get, {
            params: { voucher_date: this.batches.voucher_date }
          });
          this.batches.voucher_no = res.data.voucher_no;
        } catch (err) {
          console.error(err);
        }
      },

      async fetchTransactionCategory() {
        try {
          let res = await this.$axios.get(apiUrls.transaction_category.get);
          this.transaction_category = res.data
          // No longer chosen on this form; the voucher still records the shop's default category.
          if (this.transaction_category.length) {
            this.batches.transaction_category = this.transaction_category[0].id;
          }
        } catch (err) {
          console.error(err);
        }
      },

      async fetchParties() {
        try {
          const [customersRes, suppliersRes] = await Promise.all([
            this.$axios.get(apiUrls.customers.get),
            this.$axios.get(apiUrls.inventory_supplier.get),
          ]);
          // The API already leaves out the walk-in guest.
          this.customers = customersRes?.data || [];
          // "Opening Stock" is a placeholder supplier for stock entered with new items, not a real party.
          this.suppliers = (suppliersRes?.data || []).filter(
            (supplier) => (supplier.name || '').trim().toLowerCase() !== 'opening stock'
          );
        } catch (err) {
          console.error('Error loading customers/suppliers:', err);
        }
      },



      async submitBatch() {
        if (this.saving) return;
        if (!this.addedItems.length) {
          this.toast.warning("Please add at least one batch before submitting.");
          return;
        }

        // The cash/bank line carries the party only when every line is for the same party.
        const [first] = this.addedItems;
        const sameParty = this.addedItems.every(
          (row) => row.party_type === first.party_type && row.party_id === first.party_id
        );
        const party = sameParty && first.party_type
          ? { party_type: first.party_type, party_id: first.party_id }
          : { party_type: null, party_id: null };

        // if (parseFloat(this.paid) > parseFloat(this.totalAmount)) {
        //   this.toast.warning("Paid amount cannot exceed total amount.");
        //   return;
        // }

        // if (isNaN(this.paid) || this.paid < 0) {
        //   this.toast.warning("Invalid paid amount.");
        //   return;
        // }

        // if (this.dueAmount > 0) {
        //   const confirmed = await this.openDueModal();
        //   if (!confirmed) {
        //     this.toast.info("Submission cancelled.");
        //     return;
        //   }
        // }

        this.saving = true;
        try {

          let banks = {
              item: this.bank_account ? this.bank_account : this.cash_type,
              narration: '',
              amount: parseFloat(this.totalAmount).toFixed(2),
              type:'credit',
              reference_no: this.reference_no,
              cash_type: this.cash_type,
              bank_account: this.bank_account,
              transaction_category: this.batches.transaction_category,
              voucher_no: this.batches.voucher_no, 
              voucher_date: this.batches.voucher_date,
              flag:'dr',
              ...party,
            };

            const all_items = this.addedItems.map(({ party_name, ...row }) => ({
              ...row,
              transaction_category: this.batches.transaction_category,
              voucher_no: this.batches.voucher_no,
              voucher_date: this.batches.voucher_date,
            }));

            all_items.push(banks)

          const payload = {
            batches: all_items,
          };

          await this.$axios.post(apiUrls.journal_voucher_create.post, payload );

          this.toast.success("Expense submitted successfully!");
          this.$router.push('/account/debit/voucher/');
          this.$emit("create_success");
          this.resetForm();
          this.addedItems = [];
        } catch (error) {
          this.toast.error(apiErrorMessage(error));
          if (error.response?.data?.errors) {
            this.errors = error.response.data.errors;
          }
        } finally {
          this.saving = false;
        }
      },

      // async selectSupplier(value) {
      //   // dropdown gives only the supplier id
      //   const supplierId = value;

      //   if (!supplierId) {
      //     this.selectedSupplier = null;
      //     this.due_balance = 0;
      //     return;
      //   }

      //   // get full object from loaded array
      //   const supplierObj = this.formattedsuppliers.find((s) => s.id === supplierId);

      //   try {
      //     const response = await this.$axios.get(`/supplier/due/${supplierId}`);
      //     this.selectedSupplier = {
      //       ...supplierObj,
      //       due_balance: response.data.due_balance,
      //       name: response.data.supplier_name,
      //       phone_number: response.data.supplier_phone
      //     };
      //     this.due_balance = response.data.due_balance;
      //   } catch (error) {
      //     console.error("Error fetching due balance:", error);
      //     this.due_balance = 0;
      //   }
      // },

      cancelForm() {
        this.$router.push('/account/debit/voucher/');
      },

      removeItem(index) {
        this.addedItems.splice(index, 1);
      },

      resetForm() {
        // Reset form fields and errors
        this.batches = {
          item: '', 
          narration: '',
          // quantity: '', 
          amount: '',
        };
        this.errors = {};
        this.errorMessage = "";
        this.cash_type = this.formattedcashtypes.length > 0 ? this.formattedcashtypes[0].id : '';
        this.bank_account = null;
      }
    },

  watch: {
    'batches.voucher_date'(newDate) {
      if (newDate) {
        this.fetchReceivingNo();
      } else {
        this.batches.voucher_no = '';
      }
    },

    'cash_type'(newVal) {
      const id = newVal && typeof newVal === 'object' ? newVal.id: newVal;
      if (String(id) === '1010102') {
        if (this.bank_accounts.length > 0) {
          this.bank_account = this.bank_accounts[0].id;
        }
      } else {
        this.bank_account = null;
      }
    },

    isBank(newVal) {
      if (!newVal) this.bank_account = null;
    },

    party_type() {
      this.party_id = null;
      if (this.errors.party_id) this.errors = { ...this.errors, party_id: undefined };
    },
  },

  created() {
    this.debouncedSearch = debounce(this.searchItems, 300)
  },
};
</script>


<style scoped>
.voucher-header .header-label {
  flex: 0 0 200px;
  margin-right: 1rem;
}

.voucher-header .header-field {
  flex: 1 1 0;
  min-width: 0;
}

/* Same height as the inputs beside them */
.voucher-header :deep(.form-basic-dropdown > .btn) {
  min-height: calc(1.6em + 1rem + 2px);
  font-size: 0.875rem;
}

.voucher-header .header-error {
  min-height: 1.25rem;
  margin-left: calc(200px + 1rem);
  font-size: 0.8rem;
  color: #dc3545;
}

/* Optional: adjust table and input heights */
.table td, .table th {
  vertical-align: middle;
}

.fixed-table {
  min-height: 350px;
  max-height: 500px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 5px;
}

/* Modal Backdrop */
.confirm-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

/* Modal Box */
.confirm-modal {
  width: 420px;
  background: #ffffff;
  border-radius: 12px;
  padding: 25px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  animation: fadeInScale 0.2s ease;
}

/* Header */
.confirm-header h4 {
  text-align: center;
  margin-bottom: 20px;
  font-weight: 600;
}

/* Body Rows */
.confirm-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
}

.confirm-row.due {
  color: #dc3545;
  font-weight: 600;
  font-size: 16px;
}

/* Buttons */
.confirm-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
}

/* Animation */
@keyframes fadeInScale {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

</style>

